import React from 'react';
import { motion } from 'framer-motion';
import { Smartphone, Banknote, Layout, Globe, ArrowRight } from 'lucide-react';
import Section from '../Section';
import AnimatedFrameworks from '../AnimatedFrameworks/AnimatedFrameworks';
import GradualSpacingText from '../GradualSpacingText/GradualSpacingText';
import Button from '../Button';
import './ButlerShowcase.css';

// Highlight helpers (placed above component so they're available during render)
const renderHighlightedTitle = (text) => {
    if (!text) return null;
    const parts = String(text).split(' ');
    const highlight = parts.slice(0, 2).join(' ');
    const rest = parts.slice(2).join(' ');
    return (
        <>
            <span className="logistics-highlight">{highlight}</span>
            {rest ? <span className="logistics-title-rest"> {rest}</span> : null}
        </>
    );
};

const renderHighlightedSubtitle = (text) => {
    if (!text) return null;
    const phrase = 'shippers';
    const lower = text.toLowerCase();
    const idx = lower.indexOf(phrase);
    if (idx === -1) {
        // fallback: highlight first two words
        const parts = String(text).split(' ');
        const highlight = parts.slice(0, 2).join(' ');
        const rest = parts.slice(2).join(' ');
        return (
            <>
                <span className="logistics-sub-highlight">{highlight}</span>
                {rest ? <span> {rest}</span> : null}
            </>
        );
    }

    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + phrase.length);
    const after = text.slice(idx + phrase.length);
    return (
        <>
            {before && <span>{before}</span>}
            <span className="logistics-sub-highlight">{match}</span>
            {after && <span>{after}</span>}
        </>
    );
};

const renderFeatureTitle = (text) => {
    if (!text) return null;
    const parts = String(text).split(' ');
    const highlight = parts.slice(0, 2).join(' ');
    const rest = parts.slice(2).join(' ');
    return (
        <>
            <span className="feature-title-highlight">{highlight}</span>
            {rest ? <span className="feature-title-rest"> {rest}</span> : null}
        </>
    );
};

const renderFeatureSubtitle = (text) => {
    if (!text) return null;
    const parts = String(text).split(' ');
    const highlight = parts.slice(0, 2).join(' ');
    const rest = parts.slice(2).join(' ');
    return (
        <>
            <span className="feature-sub-highlight">{highlight}</span>
            {rest ? <span className="feature-sub-rest"> {rest}</span> : null}
        </>
    );
};

const ButlerShowcase = ({ onFeaturesClick }) => {
    // Helper to safely escape keywords for regex
    const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Highlight keywords occurrences in a description string, returning an array of React nodes
    const highlightKeywords = (text, keywords = []) => {
        if (!text || !keywords || keywords.length === 0) return text;
        // Build combined regex
        const parts = keywords.map(k => escapeRegExp(k));
        const regex = new RegExp(parts.join('|'), 'ig');

        const nodes = [];
        let lastIndex = 0;
        let match;
        while ((match = regex.exec(text)) !== null) {
            const start = match.index;
            const end = regex.lastIndex;
            if (start > lastIndex) {
                nodes.push(text.slice(lastIndex, start));
            }
            nodes.push(
                <span key={start} className="feature-sub-highlight">{text.slice(start, end)}</span>
            );
            lastIndex = end;
        }
        if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
        return nodes;
    };

    const coreFeatures = [
        {
            title: "Real-time Tracking & ELD",
            desc: "ELD & GPS tracking on all equipment with operational visibility and driver performance monitoring.",
            keywords: ['ELD', 'GPS', 'tracking'],
            icon: <Smartphone size={22} />,
        },
        {
            title: "Dedicated Lanes & Contract Freight",
            desc: "Guaranteed capacity, lane coverage, and scalable fleet support for long-term partnerships.",
            keywords: ['dedicated', 'contract', 'lane'],
            icon: <Layout size={22} />,
        },
        {
            title: "Nationwide Coverage",
            desc: "Regional distribution, long-haul lanes, port and DC freight across the U.S.",
            keywords: ['nationwide', 'port', 'DC'],
            icon: <Globe size={22} />,
        },
        {
            title: "Drop & Hook / Live Loads",
            desc: "Support for high-volume operations at distribution centers, manufacturing plants, and ports.",
            keywords: ['drop', 'hook', 'live loads'],
            icon: <Banknote size={22} />,
        }
    ];

    return (
        <Section 
            className="logistics-service-showcase reveal"
            style={{ 
                backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/hero.png)`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                position: 'relative'
            }}
        >
            <div className="logistics-bg-overlay"></div>
            <div className="logistics-header">
                <h2 className="logistics-title">
                    {renderHighlightedTitle("Freight services designed to perform")}
                </h2>
                <p className="logistics-subtitle">
                    {renderHighlightedSubtitle("Reliable dry-van capacity and freight solutions for shippers, brokers, and 3PLs across the United States.")}
                </p>
            </div>

            <div className="logistics-inner">
                {/* Text Side - Now Centered and widened */}
                <div className="logistics-text reveal">
                    <div className="feature-vertical-list">
                        {coreFeatures.map((feature, index) => (
                            <motion.div
                                key={index}
                                className="feature-row"
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <div className="feature-icon-circle">
                                    {feature.icon}
                                </div>
                                <div className="feature-info">
                                    <h4 className="feature-title">{feature.title}</h4>
                                    <p className="feature-desc">{highlightKeywords(feature.desc, feature.keywords)}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="logistics-actions">
                        <Button onClick={onFeaturesClick} variant="primary">
                            Request Quote <ArrowRight size={18} />
                        </Button>
                    </div>
                </div>
            </div>
        </Section>
    );
};

export default ButlerShowcase;